import React, { useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import logo from "../../components/Assets/logo.png";
import { API_BASE_URL } from "../../Config/config";

// Static projection component
const StaticProjection = ({ width = 200, top = "0%", showBorder = false }) => {
  return (
    <div
      className="absolute  h-[90px] opacity-80 rounded-full"
      style={{
        width: `${width}px`,
        transform: `translateX(-${width / 2}px) rotate(18deg)`,
        top,
        left: 0,
        backgroundColor: "#0041C2",
        border: showBorder ? "8px solid #87ceeb" : "none",
      }}
    />
  );
};

// Blue projections container
const BlueProjections = () => {
  const projections = [
    { width: 650, top: "0%", showBorder: false },
    { width: 950, top: "25%", showBorder: true },
    { width: 300, top: "50%", showBorder: true },
    { width: 520, top: "75%", showBorder: false },
  ];

  return (
    <div className="absolute top-0 left-0 h-full w-full overflow-hidden z-0 pointer-events-none">
      {projections.map(({ width, top, showBorder }, i) => (
        <StaticProjection
          key={i}
          width={width}
          top={top}
          showBorder={showBorder}
        />
      ))}
    </div>
  );
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendClick = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/auth/request-password-reset`, {
        email: email.trim(),
      });

      toast.success("Password reset link sent to your email!", {
        position: "top-right",
        autoClose: 3000,
        onClose: () => {
          navigate("/");
        },
      });
    } catch (error) {
      toast.error(
        "Failed to process password reset request. Please try again.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white relative overflow-hidden">
      <ToastContainer />

      {/* Static Blue Projections */}
      <BlueProjections />

      <div className="min-h-screen flex flex-col items-center justify-center px-8 relative z-10 space-y-6">
        <h1 className="text-4xl flex text-center font-sans mb-0 font-semibold">
          <span className="text-black">IRIDIS</span>
          <span className="text-[#0041C2] ml-2"> AMS</span>
        </h1>

        {/* Centered Reset Password Container  */}
        <div className="w-full max-w-2xl h-auto rounded-2xl bg-white border border-gray-300 px-16 py-12 relative">
          {/* Reset Password Form */}
          <div className="max-w-sm mt-10 mb-10 mx-auto">
            {/* Header Message */}
            <div className="mb-6 text-center">
              <p className="text-gray-800 text-2xl font-medium mb-2">
                Reset Password
              </p>
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you a link to reset your
                password. If you don’t see the email in your inbox, please check
                your Spam or Junk folder.
              </p>
            </div>

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendClick();
              }}
            >
              {/* Username Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Username
                </label>
                <input
                  id="email"
                  type="text"
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-300 focus:outline-none text-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                className={`w-full py-2 rounded-lg transition duration-200 ${
                  isLoading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white font-medium text-sm mt-6`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
          </div>

          {/* Logo with label */}
          <div className="absolute bottom-2 left-4 flex items-center space-x-3">
            <img src={logo} alt="Logo" className="w-16 h-auto" />
            <div className="text-sm text-gray-500 font-light leading-snug">
              Asset Management System
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
